import admiralQuestionsContainingPii, {
  admiralQuestionPiiQuestionAnsweredExclusionList,
} from '../Data/Maps/admiralQuestionsContainingPII';
import ValidationStatus from '../Data/ValidationStatus';
import { convertZeroToOneIndexIfNumber, deeplyEquals } from '../Utils/utils';
import {
  componentContainsCheckboxOptions,
  getOptionTier,
  getValueFromComponent,
} from './components';
import {
  isConditionallyActive,
  isHiddenAsPartOfMoveHubSupplierMatchLogic,
  isHiddenDueToSupplierMatchSelectionDisabled,
} from './conditionality';
import { handleFormError } from './handleFormError';
import { ComponentTypes } from '../Data/Components';

// TODO: Write unit tests covering all cases
export const exposeEventToPartner = ({
  eventType,
  eventObject = {},
  recursiveCall = false,
  errorObject = {},
}) => {
  if (eventType === 'formError') {
    handleFormError({ eventObject, errorObject });
  }
  /*
   All events that get sent up to the parent site of the widget need to contain an iFrameId.
   However, that iFrameId is not available instantly on widget load. It takes time to be passed in.
   To prevent the race condition where some initial events are fired without it being present,
   command buffering gets employed. Until the iFrameId becomes available, we'll buffer the events
   that come in on every function call of 'exposeEventToPartner' to bubble up later.
  */
  if (typeof window !== 'undefined') {
    if (!eventObject.iFrameId && !window?.__private__?.commandBuffer) {
      // Case 1 - To begin with no iFrameId is present and no command buffer is created yet.
      //          This logic creates the command buffer and stores this first incoming event.
      if (
        !window.__private__ ||
        typeof window.__private__ !== 'object' ||
        Array.isArray(window.__private__)
      ) {
        window.__private__ = {};
      }

      window.__private__.commandBuffer = [
        {
          function: exposeEventToPartner,
          arguments: {
            eventType,
            eventObject,
            errorObject,
          },
        },
      ];
    } else if (
      !eventObject.iFrameId &&
      window?.__private__?.commandBuffer &&
      Array.isArray(window.__private__.commandBuffer)
    ) {
      // Case 2 - There still is no iFrameId present but there now is a command buffer containing
      //          at least one event. This logic stores this next incoming subsequent event.

      // FIXME: This workaround prevents duplicate events (whilst render inefficiencies are in place across Chameleon)
      const lastCommandInQueue =
        window.__private__.commandBuffer[
          window.__private__.commandBuffer.length - 1
        ];
      // An argument comparison suffices, since all commands
      // in this buffer queue call the same function
      const lastCommandInQueueIsIdenticalToThisOne =
        lastCommandInQueue &&
        deeplyEquals(lastCommandInQueue.arguments, { eventType, eventObject });

      if (lastCommandInQueueIsIdenticalToThisOne) {
        return undefined;
      }

      window.__private__.commandBuffer.push({
        function: exposeEventToPartner,
        arguments: {
          eventType,
          eventObject,
          errorObject,
        },
      });
    } else if (
      eventObject.iFrameId &&
      window?.__private__?.commandBuffer &&
      Array.isArray(window.__private__.commandBuffer) &&
      window.__private__.commandBuffer.length > 0
    ) {
      // Case 3 - The iFrameId is now present and all buffered commands are still to be executed.
      //          This block will send the events up one by one (inserting the new iFrameId before doing so)
      //          Incoming commands will no longer need buffering after this.

      // Before running the queued commands, we need to add the incoming command to the end of the existing queue
      // The if statement prevents that once we've started clearing command after command from the queue that they get added straight back on
      if (!recursiveCall) {
        window.__private__.commandBuffer.push({
          function: exposeEventToPartner,
          arguments: {
            eventType,
            eventObject,
            errorObject,
          },
        });
      } else {
        // The buffer has started emptying but still has items in it
        // We have an iframeId now so we can send the message to the window
        const message = `${eventType}:${JSON.stringify(eventObject)}`;
        // Post message to the immediate parent of the current iframe window as this should be where the formLoader exists.
        if (window.parent) {
          window.parent.postMessage(message, '*');
        }
      }

      while (window.__private__.commandBuffer.length > 0) {
        /**
         * Since all commands inside the commandBuffer recursively call this function, we need to shrink the buffer on every call to prevent an infinite loop that uses up all stack space
         */
        const commandAndArgs = window.__private__.commandBuffer.shift();
        if (
          commandAndArgs.function &&
          commandAndArgs.arguments &&
          commandAndArgs.arguments.eventType &&
          commandAndArgs.arguments.eventObject
        ) {
          commandAndArgs.arguments.recursiveCall = true;
          /**
           * recursively call each function in the buffer queue until the buffer is empty
           *
           * This is done recursively to prevent the buffer from growing too large and causing a stack overflow. The arguments is an object that may consist of an eventObject or an errorObject as well a the eventType, but it is open to any other arguments that are passed in for the specific function being called.
           */
          commandAndArgs.function({
            eventType: commandAndArgs.arguments.eventType,
            eventObject: {
              ...commandAndArgs.arguments.eventObject,
              ...{ iFrameId: eventObject.iFrameId }, // i.e. eventObject.iFrameId is now present
            },
            recursiveCall: true,
          });
        }
      }
    } else if (
      eventObject.iFrameId &&
      (window?.__private__?.commandBuffer?.length === 0 ||
        !window?.__private__?.commandBuffer ||
        !Array.isArray(window.__private__?.commandBuffer))
    ) {
      // Case 4 - Lastly, the iFrameId is present and all buffered commands were executed or there never were any
      //          commands on hold. New incoming events can now instantly be bubbled up via this logic
      const message = `${eventType}:${JSON.stringify(eventObject)}`;
      // Post message to the immediate parent of the current iframe window as this should be where the formLoader exists.
      if (window.parent) {
        window.parent.postMessage(message, '*');
      }
    }
  }
  return undefined;
};

export const exposeFormErrorEvent = ({
  component,
  iFrameId,
  errorObject,
  formId,
}) => {
  const getFormErrorType = () => {
    const errorType = Object.keys(component.validation.constraints).filter(
      (constraint) =>
        component.validation.constraints[constraint].status ===
        ValidationStatus.INVALID
    );
    return errorType[0];
  };

  const eventObject = {
    // Convert zero based numbering to partner readable one based numbering.
    pageNumber: component.component.page + 1,
    componentInputType: component.input.type,
    componentId: component.component.id,
    iFrameId,
    formId,
    errorType: getFormErrorType(),
    currentQuestion: component.component.text,
  };

  exposeEventToPartner({
    eventType: 'formError',
    eventObject,
    errorObject,
  });
};

const shouldObfuscateValue = ({ isDomainInternal, mappedQuestionId }) => {
  const isInternalDomainAndOnPIIExclusionList =
    isDomainInternal &&
    admiralQuestionPiiQuestionAnsweredExclusionList.includes(mappedQuestionId);

  if (
    !isInternalDomainAndOnPIIExclusionList &&
    admiralQuestionsContainingPii.includes(mappedQuestionId)
  ) {
    return true;
  }

  return false;
};

const getQuestionAnsweredEventValueFromComponent = ({ component }) => {
  const value = getValueFromComponent(component);

  if (componentContainsCheckboxOptions(component)) {
    return value.join(', ');
  }

  return value;
};

export const exposeQuestionAnsweredEvent = ({
  component,
  iFrameId,
  isDomainInternal,
}) => {
  const shouldValueBeObfuscated = shouldObfuscateValue({
    isDomainInternal,
    mappedQuestionId: component.component.mappedQuestionId,
  });

  const isPII = admiralQuestionsContainingPii.includes(
    component.component.mappedQuestionId
  );

  const valueVisibility = isPII ? 'hidden' : 'visible';

  const eventObject = {
    name: component.component.text,
    inputType: component.input.type,
    componentId: component.component.id,
    pageNumber: component.component.page + 1, // Convert zero based numbering to partner readable one based numbering.
    iFrameId,
    value: shouldValueBeObfuscated
      ? '********'
      : getQuestionAnsweredEventValueFromComponent({
          component,
        }),
    valueVisibility, // If on the exclusion list, obfuscate the value in the translation layer rather than here
    visitorTier: getOptionTier(component) || '',
  };
  exposeEventToPartner({
    eventType: 'questionAnswered',
    eventObject,
    errorObject: {},
  });
};

export const exposeThankYouPageReachedEvent = ({
  iFrameId,
  submissionId,
  showCustomerMatchesOnThankYouPage,
}) => {
  exposeEventToPartner({
    eventType: 'thankYouPageReached',
    eventObject: {
      iFrameId,
      submissionId,
      showCustomerMatchesOnThankYouPage,
    },
  });
};

export const requestIdentityUpdateEvent = (iFrameId) => {
  exposeEventToPartner({
    eventType: 'userIdentityUpdateRequested',
    eventObject: { iFrameId },
  });
};

const isVisibleToCustomer = (
  components,
  component,
  subcategoryId,
  phoneNumberCountryCode
) => {
  return (
    isConditionallyActive(components, component) &&
    component.input?.hiddenInChameleon !== true &&
    component.component?.isVisible !== false &&
    component.component?.type !== ComponentTypes.HIDDEN &&
    !isHiddenAsPartOfMoveHubSupplierMatchLogic({
      component,
      components,
      subcategoryId,
      phoneNumberCountryCode,
    }) &&
    !isHiddenDueToSupplierMatchSelectionDisabled(component)
  );
};

export const formatComponentData = ({
  currentPage,
  components,
  subcategoryId,
  phoneNumberCountryCode,
}) => {
  const formComponents = {};
  const pageComponents = [];
  Object.values(components).forEach((component) => {
    const componentId = component.component.id;
    if (componentId) {
      formComponents[componentId] = {
        name: component?.component?.text,
        inputType: component?.component?.type,
        page: component.component.page + 1, // Convert zero indexed pages.
        isVisible: isVisibleToCustomer(
          components,
          component,
          subcategoryId,
          phoneNumberCountryCode
        ),
      };

      // Send all components on the page including hidden components.
      if (component.component.page === currentPage) {
        pageComponents.push(componentId);
      }
    }
  });

  // Convert zero based numbering to partner readable one based numbering.
  return {
    pageComponents,
    formComponents,
  };
};

export const exposeUserActionEvent = ({ type, iFrameId, actionDetails = {} }) =>
  exposeEventToPartner({
    eventType: 'userAction',
    eventObject: {
      type,
      iFrameId,
      actionDetails,
    },
  });

export const exposePageChangedEvent = ({
  fromPage,
  destinationPage,
  components,
  iFrameId,
  submissionPage,
  subcategoryId,
  phoneNumberCountryCode,
  emailConsentStatementShown,
  isFinalPageBeforeSubmission,
}) =>
  exposeEventToPartner({
    eventType: 'pageChanged',
    eventObject: {
      fromPage: convertZeroToOneIndexIfNumber(fromPage),
      destinationPage: convertZeroToOneIndexIfNumber(destinationPage),
      submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
      emailConsentStatementShown,
      iFrameId,
      isFinalPageBeforeSubmission,
      ...formatComponentData({
        currentPage: destinationPage,
        components,
        subcategoryId,
        phoneNumberCountryCode,
      }),
    },
  });

export const exposeInitialWidgetLoadEvent = ({
  currentPage,
  components,
  iFrameId,
  widgetLabel,
  subcategoryName,
  subcategoryId,
  categoryName,
  categoryId,
  sectorName,
  tradingHubId,
  tradingHubName,
  visitorId,
  submissionPage,
  phoneNumberCountryCode,
  componentExperiments,
}) =>
  exposeEventToPartner({
    eventType: 'initialWidgetLoad',
    eventObject: {
      currentPage: convertZeroToOneIndexIfNumber(currentPage),
      iFrameId,
      widgetLabel,
      subcategoryName,
      subcategoryId,
      categoryName,
      categoryId,
      sectorName,
      tradingHubId,
      tradingHubName,
      visitorId,
      submissionPage: convertZeroToOneIndexIfNumber(submissionPage),
      componentExperiments,
      ...formatComponentData({
        currentPage,
        components,
        subcategoryId,
        phoneNumberCountryCode,
      }),
    },
  });

export const exposeFailedForwardPageNavigationEvent = ({
  iFrameId,
  currentPage,
}) =>
  exposeEventToPartner({
    eventType: 'failedForwardPageNavigation',
    eventObject: {
      iFrameId,
      currentPage: currentPage + 1,
    },
  });

const customerMatchLevelNameMap = {
  LIVE: 'live',
  SEVEN_DAY: '7day',
  PREDEFINED: 'predefined',
  SUBCATEGORY_REVENUE_FALLBACK: 'subcategoryFallback',
};

export const convertCustomerMatchLevelToEventValue = (name) => {
  if (Object.keys(customerMatchLevelNameMap).includes(name)) {
    return customerMatchLevelNameMap[name];
  }
};

export const exposeSupplierMatchesRequestedEvent = ({
  eventName,
  iFrameId,
  isSuccess,
  maxMatches,
  customerMatchLevel,
  isMatchImpliedByPrivacyStatement,
  supplierMatches = [],
}) => {
  const brandNamesString = supplierMatches.map((match) => {
    if (match.brandId && match.brandLabel) {
      return match.brandLabel;
    }

    return match.brandFilterValue;
  });

  const brandMatches = supplierMatches
    .filter((match) => match.brandId && match.brandLabel)
    .map((match) => ({
      id: match.brandId,
      name: match.brandLabel,
    }));

  const customerMatchLevelAsEventValue =
    convertCustomerMatchLevelToEventValue(customerMatchLevel);

  exposeEventToPartner({
    eventType: eventName,
    eventObject: {
      iFrameId,
      isSuccess,
      maxMatches,
      matches: brandNamesString,
      brandMatches,
      matchLevel: customerMatchLevelAsEventValue,
      isMatchImpliedByPrivacyStatement,
    },
  });
};

/**
 * Converts an array of customer brand matches to a string of brand names.
 * @param {*} param0
 * @returns
 */
export const toCustomerBrandMatchesEventBody = ({ customerBrandMatches }) => {
  if (
    !customerBrandMatches ||
    !Array.isArray(customerBrandMatches) ||
    customerBrandMatches.length === 0
  ) {
    return [];
  }

  const brandOptionAsBrandEventBody = customerBrandMatches.map((match) => {
    if (match.id && match.value) {
      // i.e. supplier match component option values...
      return { id: match.id, name: match.value };
    }

    if (match.brandId && match.brandLabel) {
      // i.e. supplier and customer match brand values...
      return { id: match.brandId, name: match.brandLabel };
    }

    return undefined;
  });

  return brandOptionAsBrandEventBody.filter((match) => match !== undefined);
};
